import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { SearchTextField } from "@/controls/quick-filter-textfield/SearchTextField";
import { useGridStyles } from "@/pages/laboratory/styles/gridStyles";
import { useContextMenuMESF } from "@dexteel/mesf-core";
import { Button, Grid2 as Grid, Typography } from "@mui/material";
import { GridApi } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useState } from "react";
import { useGetMenuOptions } from "../hooks/useGetMenuOptions";
import { useMoldOptionsFunctions } from "../hooks/useMoldOptionsFunctions";
import { useTableData } from "../hooks/useTableData";
import { CreateMold, DeleteMold, EditMold } from "./modals";

type Props = {
	getCatalogMoldsFromAPI: Function;
	isLoading: boolean;
};

export const TableMold = ({ getCatalogMoldsFromAPI, isLoading }: Props) => {
	const classes = useGridStyles();
	const [selectedRowId, setSelectedRowId] = useState("");
	const [showCreateModal, setShowCreateModal] = useState(false);
	const [showEditModal, setShowEditModal] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [moldId, setMoldId] = useState<number | null>(null);
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [quickFilterText, setQuickFilterText] = useState("");

	const { getMenuOptions } = useMoldOptionsFunctions({
		setMoldId,
		setShowEditModal,
		setShowDeleteModal,
		setShowCreateModal,
	});

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { rows, columnDefs } = useTableData({
		setMoldId,
		setShowEditModal,
		setShowDeleteModal,
		showContextMenu,
	});

	const { columnDefaults } = useGridDefinitions({
		OnEdit: (data: any) => {
			setShowEditModal(!showEditModal);
			setMoldId(data.MoldId);
		},
	});

	const getContextMenuItems = useGetMenuOptions({
		rows,
		selectedRowId,
		setSelectedRowId,
		showContextMenu,
	});

	columnDefaults.floatingFilter = false;

	const onRowDoubleClicked = (event: any) => {
		setMoldId(event.data.MoldId);
		setShowEditModal(true);
	};

	useEffect(() => {
		if (isLoading) {
			gridApi?.showLoadingOverlay();
		} else {
			gridApi?.hideOverlay();
		}
	}, [isLoading, rows, gridApi]);

	useEffect(() => {
		registerConfig({
			id: "moldContext",
			getOptions: getMenuOptions,
		});
	}, []);

	return (
		<>
			<Grid size={{ xs: 12 }} container justifyContent="center" spacing={1}>
				<Grid size={{ xs: 12, md: 12 }}>
					<Typography variant="h5" style={{ fontWeight: 600 }}>
						Molds
					</Typography>
				</Grid>

				<Grid
					size={{ xs: 12 }}
					container
					justifyContent="flex-end"
					alignContent="center"
				>
					<Grid size={{ xs: 12 }} style={{ marginBottom: 10 }}>
						<SearchTextField
							value={quickFilterText}
							onChange={(text) => setQuickFilterText(text)}
							label="Search molds"
						/>
					</Grid>
					<Grid
						size={{ xs: 12, md: 12 }}
						style={{ height: "68vh" }}
						className={classes.borderAgGridTable}
					>
						<div style={{ height: "100%", width: "100%" }}>
							<AgGridReact
								rowData={rows || []}
								columnDefs={columnDefs}
								getContextMenuItems={getContextMenuItems}
								onRowDoubleClicked={onRowDoubleClicked}
								getRowId={(params) => params.data.MoldId}
								onGridReady={(params) => setGridApi(params.api)}
								enableCellTextSelection={true}
								suppressContextMenu={false}
								allowContextMenuWithControlKey={false}
								rowSelection="single"
								quickFilterText={quickFilterText}
							/>
						</div>
					</Grid>

					<Grid size={{ xs: 12, md: "auto" }} style={{ marginTop: 10 }}>
						<Button
							variant="contained"
							color="primary"
							fullWidth
							style={{ minWidth: 250 }}
							onClick={() => setShowCreateModal(!showCreateModal)}
						>
							New Mold
						</Button>
					</Grid>
				</Grid>
			</Grid>

			<CreateMold
				show={showCreateModal}
				onHide={(moldCreated) => {
					setShowCreateModal(false);
					setTimeout(() => {
						if (gridApi && moldCreated) {
							gridApi.paginationGoToLastPage();
							getCatalogMoldsFromAPI();
						}
					}, 100);
				}}
			/>
			<EditMold
				show={showEditModal}
				onHide={(updated: boolean) => {
					setShowEditModal(false);
					setMoldId(null);
					if (updated) getCatalogMoldsFromAPI();
				}}
				moldId={moldId}
			/>
			<DeleteMold
				show={showDeleteModal}
				onHide={(updated: boolean) => {
					setShowDeleteModal(false);
					setMoldId(null);
					if (updated) getCatalogMoldsFromAPI();
				}}
				moldId={moldId}
			/>
		</>
	);
};
